/**
 * Reiter *Geld* — die Vertragsliste (`contract-list`, Spec 03, „Reiter Geld
 * — die Vertragsliste"). Dieselbe Komponente an vier Objektarten: Am
 * Grundstück, Gebäude und an der Einheit zeigt sie den Teilbaum
 * (`?subject_id=`), am Mieter dessen Verträge (`?tenant_id=`) — welche der
 * beiden Abfragen läuft, entscheidet `routes/ObjectDetail.tsx` (Container pro
 * Bildschirm besitzt die Query, Spec 02a-Muster), diese Komponente bekommt
 * nur das Ergebnis.
 *
 * `contract-add` ist **die primäre Aktion dieses Reiters**, nicht des Kopfs
 * der Objektansicht (Spec 03, „Warum der Knopf im Reiter sitzt") — deshalb
 * lebt er hier und nicht in `routes/ObjectDetail.tsx`s Kopfzeile. Er
 * erscheint nicht am Mieter und nicht am archivierten Objekt; beides
 * entscheidet der Aufrufer über `canAdd`, nicht diese Komponente selbst (sie
 * kennt `ObjectHead.archived_at` nicht).
 */

import type { InfiniteData, UseInfiniteQueryResult, UseQueryResult } from '@tanstack/react-query'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { ApiError } from '@/lib/api'
import type { ContractListItem } from '@/features/contracts/api'

export interface ContractsPage {
  readonly items: ContractListItem[]
  readonly next_cursor: string | null
}

interface ContractListProps {
  readonly query: UseQueryResult<ContractsPage> | UseInfiniteQueryResult<InfiniteData<ContractsPage>>
  /** Das geöffnete Objekt — entscheidet, ob die Spalte „Objekt" je Zeile
   * erscheint (Spec 03: „nur wenn es nicht das geöffnete Objekt ist"). Am
   * Mieter ist `subject.id` nie gleich dieser Id, die Spalte steht dort also
   * immer — ohne Sonderfall. */
  readonly currentEntityId: string
  readonly canAdd: boolean
  readonly onAddClick: () => void
  readonly onOpenContract: (contractId: string) => void
}

export function ContractList({ query, currentEntityId, canAdd, onAddClick, onOpenContract }: ContractListProps) {
  const accessError = query.error instanceof ApiError && (query.error.isForbidden || query.error.isUnauthenticated)
  const hasReadableData = query.data !== undefined && !accessError
  const pageItems = !hasReadableData ? [] : 'pages' in query.data ? query.data.pages.flatMap((page) => page.items) : query.data.items
  const items = [...new Map(pageItems.map((item) => [item.id, item])).values()]
  const pagedQuery = 'fetchNextPage' in query ? query : undefined

  return (
    <div data-testid="contract-list">
      {query.isPending && <SkeletonRows testId="contract-list-loading" rows={3} />}

      {(accessError || (query.isError && !hasReadableData)) && (
        <ErrorState
          testId="contract-list-error"
          retryTestId="contract-list-retry"
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {hasReadableData && items.length === 0 && (
        <EmptyState
          testId="contract-list-empty"
          title="Noch kein Vertrag."
          action={
            canAdd && (
              <button
                type="button"
                data-testid="contract-add"
                onClick={onAddClick}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out)"
              >
                Vertrag anlegen
              </button>
            )
          }
        />
      )}

      {hasReadableData && items.length > 0 && (
        <>
          {canAdd && (
            <div className="flex justify-end px-(--page-x) py-2">
              <button
                type="button"
                data-testid="contract-add"
                onClick={onAddClick}
                className="border-border text-text hover:bg-bg rounded-card text-body border px-4 transition-colors duration-(--duration-fast) ease-(--ease-out)"
              >
                + Vertrag
              </button>
            </div>
          )}
          <ul>
            {items.map((contract) => (
              <ContractRow
                key={contract.id}
                contract={contract}
                currentEntityId={currentEntityId}
                onOpenContract={onOpenContract}
              />
            ))}
          </ul>
          {pagedQuery?.hasNextPage && (
            <button
              type="button"
              data-testid="object-related-contracts-more"
              disabled={pagedQuery.isFetchingNextPage}
              onClick={() => {
                void pagedQuery.fetchNextPage()
              }}
              className="border-border text-text m-2 self-start border px-3"
            >
              Mehr laden
            </button>
          )}
        </>
      )}
    </div>
  )
}

function ContractRow({
  contract,
  currentEntityId,
  onOpenContract,
}: {
  readonly contract: ContractListItem
  readonly currentEntityId: string
  readonly onOpenContract: (contractId: string) => void
}) {
  const showSubject = contract.subject.id !== currentEntityId

  return (
    <li className="border-border border-b last:border-b-0">
      <button
        type="button"
        data-testid={`contract-row-${contract.id}`}
        onClick={() => {
          onOpenContract(contract.id)
        }}
        className="hover:bg-bg flex min-h-(--row-h) w-full flex-col gap-1 px-(--page-x) py-2 text-left transition-colors duration-(--duration-fast) ease-(--ease-out) md:h-(--row-h) md:flex-row md:items-center md:gap-3 md:py-0"
      >
        <div className="flex items-center justify-between gap-2 md:flex-1 md:justify-start">
          <span className="text-body text-text font-medium">{contract.tenant.label}</span>
          <span data-numeric className="text-body text-text md:hidden">
            {contract.total_display}
          </span>
        </div>
        {showSubject && <span className="text-label text-muted md:flex-1">{contract.subject.label}</span>}
        <span className="text-label text-muted md:flex-1">{contract.period_display}</span>
        <span className="border-border text-label text-muted rounded-card self-start border px-2 py-0.5 md:self-auto">
          {contract.status.label}
        </span>
        <span data-numeric className="text-body text-text hidden md:block md:w-24">
          {contract.total_display}
        </span>
      </button>
    </li>
  )
}
